<template>
  <div class="ls-gate">
    <div
      v-if="state === 'pending'"
      class="ls-gate__pending"
      aria-busy="true"
      aria-label="Loading the line sheet"
    />
    <branded-line-sheet
      v-else-if="state === 'branded'"
      :key="shown"
      :presentation="presentation"
      :public-token="publicToken"
      :tracked-link-id="trackedLinkId"
      :plg-base-url="plgBaseUrl()"
      @browse-all="setState('browse')"
    />
    <template v-else>
      <div
        v-if="state === 'browse'"
        class="ls-gate__back"
        data-test="ls-back"
      >
        <button
          type="button"
          @click="setState('branded')"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            aria-hidden="true"
            focusable="false"
          ><path
            d="M15 6l-6 6 6 6"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          /></svg>
          Back to the lookbook
        </button>
      </div>
      <slot />
    </template>
  </div>
</template>

<script>
import { Settings } from '@/Settings'
import { fetchLineSheetPresentation, presentationQuery } from './useLineSheetPresentation'

/**
 * Decides, once per visit, which page a public line sheet link opens (KP-4545):
 * the brand's lookbook when plg-go-be has a ready presentation for the token,
 * otherwise today's page (the default slot), untouched. It waits at most the
 * presentation budget (1.2 s) and shows a blank frame meanwhile, so the buyer
 * never sees the plain page flash before the branded one.
 *
 * Emits `variant` with branded | plain | browse so the route can keep its own
 * chrome (mesh, cookie bar, favicon) for the plain page only.
 */
export default {
  name: 'LineSheetGate',
  components: {
    BrandedLineSheet: () => import(/* webpackChunkName: "branded-line-sheet" */ './BrandedLineSheet.vue'),
  },
  props: {
    publicToken: { type: String, required: true },
    /** The route's query: carries the tracked link id (ks) and studio version (lsv). */
    query: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      state: 'pending', presentation: null, requestSeq: 0, shown: 0,
    }
  },
  computed: {
    // Only the query keys the presentation depends on (ks, lsv).
    presentationKey() {
      return JSON.stringify(presentationQuery(this.query || {}))
    },
    // KP-4572: feedback from an email link visitor is theirs, no prompt.
    trackedLinkId() {
      return presentationQuery(this.query || {}).trackedLinkId
    },
  },
  watch: {
    presentationKey() {
      this.refresh()
    },
  },
  created() {
    this.refresh()
  },
  methods: {
    // The latest request wins: an older answer arriving late is dropped.
    async refresh() {
      this.requestSeq += 1
      const seq = this.requestSeq
      const presentation = await this.load()
      if (seq !== this.requestSeq) return
      // A new key per accepted presentation remounts the lookbook, so its
      // fonts and tab branding follow the new version.
      this.shown += 1
      this.presentation = presentation
      // A buyer browsing all styles stays there; Back opens the new version.
      if (this.state === 'browse' && presentation) return
      this.setState(presentation ? 'branded' : 'plain')
    },
    plgBaseUrl() {
      try {
        return Settings.getOptionalConfig('VUE_APP_PLG_URL') || ''
      } catch (e) {
        return ''
      }
    },
    load() {
      return fetchLineSheetPresentation({
        plgBaseUrl: this.plgBaseUrl(),
        token: this.publicToken,
        ...presentationQuery(this.query || {}),
      })
    },
    setState(state) {
      this.state = state
      this.$emit('variant', state)
    },
  },
}
</script>

<style lang="scss" scoped>
.ls-gate__pending {
  min-height: 100vh;
  background: #fff;
}

.ls-gate__back {
  position: relative;
  z-index: 5;
  display: flex;
  padding: 8px 16px;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 40px;
    padding: 0 12px;
    border: 0;
    background: transparent;
    color: #16130f;
    font-weight: 600;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid #16130f;
      outline-offset: 2px;
    }
  }
}
</style>
